import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { TokenPayload } from "../../types/constants.types";
import { decodeToken, getToken, setToken } from "./TokenUtility";
import config from "../../config";
import { notifyOwnerOnboardingMayHaveChanged } from "../ownerOnboardingEvents";

const BASE_URL = config.apiBaseUrl;

type FailedRequests = {
	resolve: (value: AxiosResponse) => void;
	reject: (values: AxiosError) => void;
	config: AxiosRequestConfig;
	error: AxiosError;
};

let failedRequests: FailedRequests[] = [];
let isTokenRefreshing = false;

const forceLogoutAndRedirect = () => {
	setToken("accessToken", "");
	setToken("refreshToken", "");
	localStorage.removeItem("user");
	if (window.location.pathname !== "/login") {
		window.location.replace("/login");
	}
};

const customFetch = axios.create({
	baseURL: BASE_URL,
	headers: {
		"Content-type": "application/json",
		"app-type": "mobile",
	},
	withCredentials: false,
});

customFetch.interceptors.request.use((request) => {
	const accessToken = getToken("accessToken");
	const tokenValue = accessToken?.token || "";

	// Getting Expiered status
	var expiery = 0;
	if (tokenValue.length > 0) {
		const decodedToken: TokenPayload = decodeToken(tokenValue);
		var expiery = (decodedToken.exp || 0) * 1000;
	}
	const currentTime = new Date().getTime();
	if (
		request.url &&
		request.url?.indexOf("/auth/refresh") < 0 &&
		tokenValue &&
		expiery >= currentTime
	) {
		request.headers["Authorization"] = "Bearer " + tokenValue;
	}
	return request;
});

customFetch.interceptors.response.use(
	(response) => {
		notifyOwnerOnboardingMayHaveChanged(response.config?.method, response.config?.url);
		let accessToken = response.data?.accessToken;
		let refreshToken = response.data?.refreshToken;
		if (accessToken) {
			let userObj = decodeToken(accessToken);
			const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
			userObj = {
				...existingUser,
				...userObj,
				authId: userObj.sub || existingUser.authId || existingUser.id,
			};
			setToken("accessToken", accessToken);
			localStorage.setItem("user", JSON.stringify(userObj));
		}
		return response;
	},
	async (error: AxiosError) => {
		const status = error.response?.status;
		const originalRequestConfig = error.config!;
		if (status !== 401) {
			return Promise.reject(error);
		}

		if (
			originalRequestConfig?.url?.includes("/auth/forgot-password") ||
			originalRequestConfig?.url?.includes("/auth/reset-password")
		) {
			return Promise.reject(error);
		}

		if (originalRequestConfig?.url?.includes("/auth/refresh")) {
			forceLogoutAndRedirect();
			return Promise.reject(error);
		}

		if (isTokenRefreshing) {
			return new Promise((resolve, reject) => {
				failedRequests.push({
					resolve,
					reject,
					config: originalRequestConfig,
					error: error,
				});
			});
		}
		isTokenRefreshing = true;

		try {
			let storedRefreshToken = getToken("refreshToken");
			let user = localStorage.getItem("user") ?? "{}";
			let email = JSON.parse(user || "{}").email;
			if (!storedRefreshToken || !storedRefreshToken.exp) {
				forceLogoutAndRedirect();
				return Promise.reject(error);
			}
			const response = await customFetch.post("/auth/refresh", {
				email: email,
				refreshToken: storedRefreshToken,
			});
			const { accessToken = null, refreshToken = null } = response.data ?? {};

			if (!accessToken || !refreshToken) {
				throw new Error("Could not retrieve Tokens");
			}

			setToken("accessToken", accessToken);
			// localStorage.setItem("refreshToken", refreshToken);

			failedRequests.forEach(({ resolve, reject, config }) => {
				customFetch(config)
					.then((response) => {
						resolve(response);
					})
					.catch((error) => reject(error));
			});
		} catch (_error: unknown) {
			failedRequests.forEach(({ error, reject }) => {
				reject(error);
			});
			forceLogoutAndRedirect();
			// localStorage.setItem("refreshToken", "");
			return Promise.reject(error);
		} finally {
			failedRequests = [];
			isTokenRefreshing = false;
		}
		return customFetch(originalRequestConfig);
	},
);

export { customFetch };
